import { toast } from 'sonner';
import type { ProjectMember, ProjectWithPreview } from '../../lib/api/projects';
import { ProjectCard } from '../projects/ProjectCard';
import { ProjectCardSkeleton } from '../projects/ProjectCardSkeleton';
import { Example } from './primitives';
import { DEMO_PROJECT } from './demoData';

/**
 * Every state of the Projects page card (pages/projects/ProjectCard), rendered
 * from the REAL component in the REAL page grid. One source for two readers:
 * the /design-system showcase and the standalone states preview
 * (docs/prototypes/project-card-states.html, built from this file).
 */

const PEOPLE = [
  'Maya Chen', 'Jordan Lee', 'Priya Shah', 'Sam Ortiz', 'Alex Kim', 'Noor Haddad', 'Tom Becker',
  'Lina Park', 'Omar Diaz', 'Eve Laurent', 'Kai Tanaka', 'Rosa Marin', 'Ben Wolfe', 'Ida Novak',
];
const people = (n: number): ProjectMember[] =>
  PEOPLE.slice(0, n).map((name, i) => ({ id: `ds-user-${i + 1}`, name, avatarUrl: null }));

/** A flat-colour stand-in for a profile photo, so the <img> branch of Avatar shows. */
const photo = (hex: string) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><rect width="48" height="48" fill="${hex}"/><circle cx="24" cy="19" r="9" fill="#fff" opacity=".85"/><path d="M6 48c2-12 10-17 18-17s16 5 18 17z" fill="#fff" opacity=".85"/></svg>`)}`;

const ago = (ms: number) => new Date(Date.now() - ms).toISOString();
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

interface CardState {
  label: string;
  project: ProjectWithPreview;
  archived?: boolean;
}

const STATES: CardState[] = [
  // The role badge describes the VIEWER, not the project (ProjectCard.tsx role
  // chips; enrichProjectsWithCounts on the server). One team project, two viewers:
  { label: 'Team project, seen by its owner: no badge', project: { ...DEMO_PROJECT, updatedAt: ago(HOUR) } },
  { label: 'Same project, seen by a teammate: Team badge', project: { ...DEMO_PROJECT, id: 'ds-pc-teammate', previewUrl: DEMO_PROJECT.previewUrl, role: 'team', updatedAt: ago(HOUR) } },
  { label: 'Exactly 3 people: no overflow chip', project: { ...DEMO_PROJECT, id: 'ds-pc-3', name: 'Checkout Redesign', primaryDomain: 'shop.acme.com', ticketCount: 38, members: people(3), updatedAt: ago(3 * HOUR) } },
  { label: 'Private project: the owner alone', project: { ...DEMO_PROJECT, id: 'ds-pc-1', name: 'Personal Sandbox', primaryDomain: 'staging.acme.dev', ticketCount: 2, members: people(1), updatedAt: ago(2 * DAY) } },
  { label: 'Large team, 14 people: +11', project: { ...DEMO_PROJECT, id: 'ds-pc-14', name: 'Company Website', primaryDomain: 'acme.com', ticketCount: 214, members: people(14), updatedAt: ago(20 * 60 * 1000) } },
  { label: 'People with profile photos', project: { ...DEMO_PROJECT, id: 'ds-pc-photo', name: 'Mobile Web App', primaryDomain: 'm.acme.com', ticketCount: 17, members: people(4).map((m, i) => ({ ...m, avatarUrl: i < 2 ? photo(['#3468F8', '#1a8054'][i]) : null })), updatedAt: ago(5 * DAY) } },
  // A provider's generated letter picture (Google's flat-colour initial) is
  // judged server-side and stored as null, so that person renders as palette
  // initials like everyone else (server services/avatarCheck.service.ts).
  { label: 'No chosen photo: initials in the palette, never a provider letter tile', project: { ...DEMO_PROJECT, id: 'ds-pc-initials', name: 'Client Portal', primaryDomain: 'portal.acme.com', ticketCount: 31, members: [{ id: 'ds-user-ev', name: 'Emilia Veras', avatarUrl: null }, ...people(2)], updatedAt: ago(6 * HOUR) } },
  { label: 'New project: 0 issues', project: { ...DEMO_PROJECT, id: 'ds-pc-0', name: 'Docs Portal', primaryDomain: 'docs.acme.com', ticketCount: 0, members: people(2), updatedAt: ago(30 * 1000) } },
  { label: 'Shared with me: no thumbnail, no domain', project: { ...DEMO_PROJECT, id: 'ds-pc-shared', name: 'Marketing Site', primaryDomain: null, previewUrl: null, ticketCount: 4, role: 'collaborator', members: people(2).reverse(), updatedAt: ago(9 * DAY) } },
  { label: 'View only', project: { ...DEMO_PROJECT, id: 'ds-pc-viewer', name: 'Partner Portal', primaryDomain: 'partners.acme.com', ticketCount: 9, role: 'viewer', members: people(6), updatedAt: ago(14 * DAY) } },
  { label: 'Long name with a Team badge: title truncates', project: { ...DEMO_PROJECT, id: 'ds-pc-long', name: 'Enterprise Customer Onboarding Dashboard Revamp 2026', primaryDomain: 'onboarding.enterprise.acme.com', ticketCount: 1043, role: 'team', members: people(9), updatedAt: ago(45 * DAY) } },
  { label: 'Archived view', archived: true, project: { ...DEMO_PROJECT, id: 'ds-pc-arch', name: 'Spring Campaign', primaryDomain: 'spring.acme.com', ticketCount: 26, members: people(3), updatedAt: ago(120 * DAY) } },
  { label: 'No people from the API: count stays right', project: { ...DEMO_PROJECT, id: 'ds-pc-none', name: 'Legacy Intranet', primaryDomain: 'intranet.acme.com', ticketCount: 7, members: undefined, updatedAt: ago(7 * DAY) } },
];

export function ProjectCardStates() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {STATES.map(({ label, project, archived }) => (
        <Example key={project.id} label={label} block>
          <ProjectCard
            project={project}
            archived={archived}
            onOpenSettings={() => toast('Settings (demo)')}
            onArchive={() => toast('Archive (demo)')}
            onDelete={() => toast('Delete (demo)')}
            onUnarchive={() => toast('Unarchive (demo)')}
            onClientReview={() => toast('Share client link (demo)')}
            onLiveReview={() => toast('Capture issues (demo)')}
          />
        </Example>
      ))}
      <Example label="Loading skeleton: same height as a loaded card" block>
        <ProjectCardSkeleton />
      </Example>
    </div>
  );
}
